import {
  AudioLines,
  Banana,
  Calendar,
  Cloud,
  GraduationCap,
  Handshake,
  HelpCircle,
  Home,
  IdCard,
  Music2,
  Radio,
  Sparkles,
  Target,
  Users,
  Wand2,
  Zap,
} from 'lucide-react';
import { DiscordIcon } from './DiscordIcon';

// The navigation of the hacker portal sidebar, one table instead of the four
// app/(hack)/**/hacker/(protected)/_components/hacker-sidebar.tsx twins the Next tree
// keeps (PORTING.md G5). Everything that differed between them — which entries exist,
// where they point, whether a sponsor entry prints a wordmark instead of an icon — is
// data here; the markup is HackerSidebar.tsx.

export type SidebarLogo = {
  src: string;
  aspect: string;
  heightClass: string;
};

export type NavItem = {
  kind: 'item';
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  external: boolean;
  logo?: SidebarLogo;
  // Listed but not live yet: drawn greyed out and not clickable.
  comingSoon?: boolean;
};

export type NavGroup = {
  kind: 'group';
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  children: NavItem[];
};

export type NavEntry = NavItem | NavGroup;

type SidebarEdition = {
  // Entries above the sponsors group, in order.
  items: NavItem[];
  // The Música entry, shown from the moment the event starts. Absent for editions whose
  // sidebar never links it (the generic one, and 26-co — see the note below).
  music?: NavItem;
  sponsors?: NavGroup;
};

const TRACK_SELECTION: NavItem = {
  kind: 'item',
  name: 'Selección de Tracks',
  href: '/hacker/track-selection',
  icon: Target,
  external: false,
};

const MENTOR_SELECTION: NavItem = {
  kind: 'item',
  name: 'Selección de Mentores',
  href: '/hacker/mentor-selection',
  icon: GraduationCap,
  external: false,
};

const ANTHROPIC_LOGO: SidebarLogo = {
  src: '/assets/logos/anthropic.svg',
  aspect: '1024.2 / 115',
  heightClass: 'h-4',
};

const ELEVENLABS_LOGO: SidebarLogo = {
  src: '/assets/logos/elevenlabs.svg',
  aspect: '694 / 90',
  heightClass: 'h-4',
};

const KAPSO_LOGO: SidebarLogo = {
  src: '/assets/logos/kapso.svg',
  aspect: '6180 / 1948',
  heightClass: 'h-5',
};

const RENDER_LOGO: SidebarLogo = {
  src: '/assets/logos/render.svg',
  aspect: '110 / 21',
  heightClass: 'h-4',
};

function sponsorGroup(children: NavItem[]): NavGroup {
  return { kind: 'group', name: 'Sponsors', icon: Handshake, children };
}

// The edition-agnostic sidebar: the one the generic app/(hack)/hacker/(protected)
// layout falls back to when the current event has no branded sidebar of its own.
const GENERIC: SidebarEdition = {
  items: [
    {
      kind: 'item',
      name: 'Dashboard',
      href: '/hacker/dashboard',
      icon: Home,
      external: false,
    },
    {
      kind: 'item',
      name: 'Cronograma',
      href: '/hacker/schedule',
      icon: Calendar,
      external: false,
    },
    TRACK_SELECTION,
    MENTOR_SELECTION,
  ],
};

const AR: SidebarEdition = {
  items: [
    {
      kind: 'item',
      name: 'Dashboard',
      href: '/26-ar/hacker/dashboard',
      icon: Home,
      external: false,
    },
    {
      kind: 'item',
      name: 'Cronograma',
      href: '/26-ar/hacker/schedule',
      icon: Calendar,
      external: false,
    },
    {
      kind: 'item',
      name: 'Credencial',
      href: '/26-ar/hacker/credential',
      icon: IdCard,
      external: false,
    },
    TRACK_SELECTION,
    MENTOR_SELECTION,
  ],
  music: {
    kind: 'item',
    name: 'Música',
    href: '/26-ar/hacker/music',
    icon: Music2,
    external: false,
  },
  // 26-ar's sponsor pages live outside the portal, at /26-ar/<sponsor>, and print
  // their name rather than a wordmark.
  sponsors: sponsorGroup([
    {
      kind: 'item',
      name: 'Anthropic',
      href: '/26-ar/anthropic',
      icon: Sparkles,
      external: false,
    },
    {
      kind: 'item',
      name: 'Vercel v0',
      href: '/26-ar/vercel-v0',
      icon: Wand2,
      external: false,
    },
    {
      kind: 'item',
      name: 'Kapso',
      href: '/26-ar/kapso',
      icon: Zap,
      external: false,
    },
  ]),
};

const MX: SidebarEdition = {
  items: [
    {
      kind: 'item',
      name: 'Dashboard',
      href: '/26-mx/hacker/dashboard',
      icon: Home,
      external: false,
    },
    {
      kind: 'item',
      name: 'Livestream',
      href: '/26-mx/live',
      icon: Radio,
      external: false,
    },
    {
      kind: 'item',
      name: 'Cronograma',
      href: '/26-mx/hacker/schedule',
      icon: Calendar,
      external: false,
    },
    {
      kind: 'item',
      name: 'Platanus Hack Market',
      href: '/26-mx/market',
      icon: Banana,
      external: false,
    },
    {
      kind: 'item',
      name: 'Credencial',
      href: '/26-mx/hacker/credential',
      icon: IdCard,
      external: false,
    },
    TRACK_SELECTION,
    MENTOR_SELECTION,
  ],
  music: {
    kind: 'item',
    name: 'Música',
    href: '/26-mx/hacker/music',
    icon: Music2,
    external: false,
  },
  sponsors: sponsorGroup([
    {
      kind: 'item',
      name: 'Anthropic',
      href: '/26-mx/hacker/anthropic',
      icon: Sparkles,
      external: false,
      logo: ANTHROPIC_LOGO,
    },
    {
      kind: 'item',
      name: 'ElevenLabs',
      href: '/26-mx/hacker/elevenlabs',
      icon: AudioLines,
      external: false,
      logo: ELEVENLABS_LOGO,
    },
    {
      kind: 'item',
      name: 'Kapso',
      href: '/26-mx/hacker/kapso',
      icon: Zap,
      external: false,
      logo: KAPSO_LOGO,
    },
  ]),
};

// 26-co: the pages behind these links belong to `sponsors-landing:pages`; the entries are
// here because this sidebar is what every `/hacker/*` page of the current edition wears.
// It deliberately has no Música entry — the Next source hides it while keeping the page.
const CO: SidebarEdition = {
  items: [
    {
      kind: 'item',
      name: 'Dashboard',
      href: '/26-co/hacker/dashboard',
      icon: Home,
      external: false,
    },
    {
      kind: 'item',
      name: 'Livestream',
      href: '/26-co/live',
      icon: Radio,
      external: false,
    },
    {
      kind: 'item',
      name: 'Cronograma',
      href: '/26-co/hacker/schedule',
      icon: Calendar,
      external: false,
    },
    {
      kind: 'item',
      name: 'Platanus Hack Market',
      href: '/26-co/market',
      icon: Banana,
      external: false,
    },
    {
      kind: 'item',
      name: 'Credencial',
      href: '/26-co/hacker/credential',
      icon: IdCard,
      external: false,
    },
    TRACK_SELECTION,
    MENTOR_SELECTION,
    {
      kind: 'item',
      name: 'Asistentes',
      href: '/26-co/hacker/attendees',
      icon: Users,
      external: false,
    },
  ],
  sponsors: sponsorGroup([
    {
      kind: 'item',
      name: 'Anthropic',
      href: '/26-co/hacker/anthropic',
      icon: Sparkles,
      external: false,
      logo: ANTHROPIC_LOGO,
    },
    {
      kind: 'item',
      name: 'ElevenLabs',
      href: '/26-co/hacker/elevenlabs',
      icon: AudioLines,
      external: false,
      logo: ELEVENLABS_LOGO,
    },
    {
      kind: 'item',
      name: 'Kapso',
      href: '/26-co/hacker/kapso',
      icon: Zap,
      external: false,
      logo: KAPSO_LOGO,
    },
    {
      kind: 'item',
      name: 'Render',
      href: '/26-co/hacker/render',
      icon: Cloud,
      external: false,
      logo: RENDER_LOGO,
    },
  ]),
};

const VE: SidebarEdition = {
  items: [
    {
      kind: 'item',
      name: 'Dashboard',
      href: '/26-ve/hacker/dashboard',
      icon: Home,
      external: false,
    },
    {
      kind: 'item',
      name: 'Cronograma',
      href: '/26-ve/hacker/schedule',
      icon: Calendar,
      external: false,
    },
    {
      kind: 'item',
      name: 'Platanus Hack Market',
      href: '/26-ve/market',
      icon: Banana,
      external: false,
    },
    {
      kind: 'item',
      name: 'Credencial',
      href: '/26-ve/hacker/credential',
      icon: IdCard,
      external: false,
    },
    TRACK_SELECTION,
    MENTOR_SELECTION,
    {
      kind: 'item',
      name: 'Asistentes',
      href: '/26-ve/hacker/attendees',
      icon: Users,
      external: false,
    },
  ],
  // Linked from the start of the event; the page itself says so when the bot is off.
  music: {
    kind: 'item',
    name: 'Música',
    href: '/26-ve/hacker/music',
    icon: Music2,
    external: false,
  },
  sponsors: sponsorGroup([
    {
      kind: 'item',
      name: 'Anthropic',
      href: '/26-ve/hacker/anthropic',
      icon: Sparkles,
      external: false,
      logo: ANTHROPIC_LOGO,
    },
    {
      kind: 'item',
      name: 'ElevenLabs',
      href: '/26-ve/hacker/elevenlabs',
      icon: AudioLines,
      external: false,
      logo: ELEVENLABS_LOGO,
    },
    {
      kind: 'item',
      name: 'Kapso',
      href: '/26-ve/hacker/kapso',
      icon: Zap,
      external: false,
      logo: KAPSO_LOGO,
    },
    {
      kind: 'item',
      name: 'Render',
      href: '/26-ve/hacker/render',
      icon: Cloud,
      external: false,
      logo: RENDER_LOGO,
    },
  ]),
};

const EDITIONS: Record<string, SidebarEdition> = {
  '26-ar': AR,
  '26-mx': MX,
  '26-co': CO,
  '26-ve': VE,
};

export function buildNavigation(
  edition: string | null,
  {
    discordGuildId,
    discordFaqChannelId,
    showMusic,
  }: {
    discordGuildId: string | null;
    discordFaqChannelId: string | null;
    showMusic: boolean;
  },
): NavEntry[] {
  const config = (edition && EDITIONS[edition]) || GENERIC;
  const entries: NavEntry[] = [...config.items];

  if (config.music && showMusic) {
    entries.push(config.music);
  }
  if (config.sponsors) {
    entries.push(config.sponsors);
  }
  if (discordGuildId) {
    entries.push({
      kind: 'item',
      name: 'Discord',
      href: `https://discord.com/channels/${discordGuildId}`,
      icon: DiscordIcon,
      external: true,
    });
    if (discordFaqChannelId) {
      entries.push({
        kind: 'item',
        name: 'FAQ',
        href: `https://discord.com/channels/${discordGuildId}/${discordFaqChannelId}`,
        icon: HelpCircle,
        external: true,
      });
    }
  }

  return entries;
}

export function isItemActive(pathname: string, href: string) {
  return (
    pathname === href ||
    pathname.startsWith(`${href}/`) ||
    pathname.endsWith(href)
  );
}
